import { useLocation, useParams } from "react-router";
import { useCollection, useCollectionFetching, useSpace } from "@/api/hooks";
import { useGetRecordQuery } from "@/api/api";

import { PropsWithChildren, ReactNode } from "react";
import CurrentIntervalWidget from "./CurrentIntervalWidget";
import CurrentPageWidget from "./CurrentPageWidget";

export function HeaderText({ children }: PropsWithChildren) {
  return (
    <span
      className="min-w-0 truncate text-[16px] font-semibold text-foreground"
      style={{ fontFamily: "var(--font-serif)" }}
    >
      {children}
    </span>
  );
}

/**
 * Left inset of the header's time button, chosen so the brand mark's centre
 * lands on the timeline's spine: 1px of the list's border, plus SPINE_X (24)
 * from `RecordsTimeline`, less the button's own px-2 and half the 20px mark.
 */
const BRAND_PL = 1 + 24 - 8 - 10;

export default function Header() {
  // const globalStacks = useGlobalStacks();
  const location = useLocation();
  const { collectionID, recordID } = useParams();
  const collection = useCollection();
  const collectionIsFetching = useCollectionFetching();
  const { isOwn, owner } = useSpace();
  const {
    isFetching: recordIsFetching,
    isError: recordIsError,
    data: record,
  } = useGetRecordQuery(recordID || "", {
    skip: recordID === null || recordID === undefined,
  });

  // Two controls, one per sidebar: the time button on the left, the page title
  // on the right. Every title is the collections toggle, including the ones
  // that only report a state — with the panel icons gone, a collapsed sidebar
  // would otherwise have no way back on those routes.
  let title: ReactNode = null;
  // The routes with no interval to name still show the mark, so the time
  // sidebar has a way back there too.
  let showInterval = true;

  if (collectionID) {
    if (collectionIsFetching) {
      title = "Loading...";
    } else if (!collection) {
      title = "Collection is not accessible";
    } else {
      title = collection.title;
    }
  } else if (location.pathname === "/admin") {
    showInterval = false;
    title = "Users";
  } else if (location.pathname === "/login") {
    showInterval = false;
    title = "Sign in";
  } else if (location.pathname.startsWith("/record")) {
    showInterval = false;
    if (recordID) {
      if (location.pathname.endsWith("/edit")) {
        title = "Edit record";
      } else if (recordIsFetching) {
        title = "Loading...";
      } else if (recordIsError || !record) {
        title = "Record is not accessible";
      } else {
        title = "Record";
      }
    } else {
      title = "New record";
    }
  } else {
    // `/` is not "everything" — the API scopes it to the signed-in user, and
    // `/:userID` scopes it to that person.
    title = isOwn
      ? "Your collections"
      : owner?.name
        ? `${owner.name}'s collections`
        : // Someone else's space, but we have no name to put on it —
          // "Your collections" here would claim it as the reader's own.
          "Collections";
  }
  return (
    <header className="bg-card sticky top-0 z-50 border-b min-h-[55px]">
      <div
        className="h-full flex items-center justify-between py-2 pr-2 gap-2"
        style={{ paddingLeft: BRAND_PL }}
      >
        {/* Kept as a slot even when it renders nothing, so justify-between
            still holds the title to the right edge. */}
        <div className="flex items-center min-w-0">
          <CurrentIntervalWidget showInterval={showInterval} />
        </div>
        <CurrentPageWidget>{title}</CurrentPageWidget>
      </div>
    </header>
  );
}
